import { useState } from 'react';
import { useFetch } from './useFetch';
import API_ROUTES from './apiRoutes';

interface UploadProgress {
  isUploading: boolean;
  progress: number;
  error: string | null;
  success: boolean;
}

interface NotificationData {
  title: string;
  message: string;
  image?: boolean; // Flag indicando se a notificação terá imagem
}

export const useNotificationUpload = () => {
  const [uploadState, setUploadState] = useState<UploadProgress>({
    isUploading: false,
    progress: 0,
    error: null,
    success: false,
  });

  const { fetchWithAuth } = useFetch();

  // Função para criar notificação e fazer upload da imagem (opcional) em uma única operação
  const createNotificationWithImage = async (
    notificationData: NotificationData,
    imageFile?: File | null
  ): Promise<{ notification: any; imageUrl?: string } | null> => {
    setUploadState({
      isUploading: true,
      progress: 0,
      error: null,
      success: false,
    });

    try {
      setUploadState(prev => ({ ...prev, progress: 15 }));

      // 1. Fazer POST para /notifications para obter URL presigned da imagem (se image: true)
      const notificationResponse = await fetchWithAuth(API_ROUTES.NOTIFICATIONS.create(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(notificationData),
      });

      if (!notificationResponse.ok) {
        const errorText = await notificationResponse.text();
        throw new Error(`Erro ao criar notificação: ${notificationResponse.status} - ${errorText}`);
      }

      const responseData = await notificationResponse.json();
      
      const { id, imageUrl, createdAt } = responseData;

      setUploadState(prev => ({ ...prev, progress: 30 }));

      // 2. Se houver arquivo de imagem e imageUrl, fazer upload
      let finalImageUrl: string | undefined;
      if (imageFile && imageUrl) {
        const imageUploadResponse = await fetch(imageUrl, {
          method: 'PUT',
          body: imageFile,
          headers: {
            'Content-Type': imageFile.type,
          },
        });

        if (!imageUploadResponse.ok) {
          const errorBody = await imageUploadResponse.text();
          throw new Error(`Erro no upload da imagem para S3: ${imageUploadResponse.status} - ${errorBody}`);
        }

        const baseImageUrl = imageUrl.split('?')[0];
        finalImageUrl = baseImageUrl;
        
        setUploadState(prev => ({ ...prev, progress: 90 }));
      }

      setUploadState(prev => ({ ...prev, progress: 100, success: true, isUploading: false }));

      return {
        notification: { id, createdAt },
        imageUrl: finalImageUrl
      };

    } catch (error) {
      setUploadState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isUploading: false,
      }));
      return null;
    }
  };

  const resetUploadState = () => {
    setUploadState({
      isUploading: false,
      progress: 0,
      error: null,
      success: false,
    });
  };

  return {
    createNotificationWithImage,
    uploadState,
    resetUploadState,
  };
};

